import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { TEMPLATES } from "../lib/biodata";

/**
 * Sanitize the biodata before sending it to /export/preview:
 *  - drop the potentially-huge `photos` gallery array (preview only shows the
 *    single `photo_url`); this keeps request payloads small even after a user
 *    uploads their gallery of 6 photos
 *  - drop server-only bookkeeping fields the guest export endpoint ignores
 *    but that just bloat the payload
 *  - guarantee every top-level sub-section exists (avoids Jinja "undefined"
 *    errors on freshly-created guest biodatas)
 */
function toPreviewPayload(biodata) {
  const empty = {};
  return {
    title: biodata.title || "My Biodata",
    template: biodata.template || "classic",
    privacy: biodata.privacy || "private",
    photo_url: biodata.photo_url || "",
    personal: biodata.personal || empty,
    family: biodata.family || empty,
    education_career: biodata.education_career || empty,
    lifestyle: biodata.lifestyle || empty,
    contact: biodata.contact || empty,
    partner_preferences: biodata.partner_preferences || empty,
  };
}

/** Renders the server-generated biodata HTML inside a sandboxed iframe. */
export default function BiodataPreview({ biodata }) {
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const debounceRef = useRef();
  const inFlightRef = useRef();

  const activeTpl =
    TEMPLATES.find((t) => t.id === biodata.template)?.label || biodata.template;

  useEffect(() => {
    clearTimeout(debounceRef.current);
    // Abort a previous in-flight request so we always render the newest state
    if (inFlightRef.current) {
      try {
        inFlightRef.current.abort();
      } catch {
        // no-op
      }
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setError("");
      const controller = new AbortController();
      inFlightRef.current = controller;
      try {
        const payload = toPreviewPayload(biodata);
        const { data } = await api.post("/export/preview", payload, {
          signal: controller.signal,
          timeout: 20000, // 20s — plenty for WeasyPrint HTML rendering
        });
        setHtml(data.html);
      } catch (e) {
        // Ignore user-cancelled aborts (they happen every keystroke)
        if (e?.code === "ERR_CANCELED" || e?.name === "CanceledError" || e?.name === "AbortError") {
          return;
        }
        // Surface details in the browser console so users following our error
        // hint ("Please check the console") actually find something useful.
        // eslint-disable-next-line no-console
        console.error("[BiodataPreview] Preview request failed:", e);
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        const msg = status
          ? `HTTP ${status}${detail ? ` — ${typeof detail === "string" ? detail : JSON.stringify(detail).slice(0, 160)}` : ""}`
          : e?.message || "Network error";
        setError(msg);
        setHtml(
          `<div style='padding:24px;font-family:Georgia,serif;color:#8b1e41;line-height:1.55'>
             <div style='font-size:14px;letter-spacing:2px;text-transform:uppercase;color:#8a817c;margin-bottom:8px'>Preview unavailable</div>
             <div style='font-size:18px;margin-bottom:8px'>We couldn&#39;t refresh the preview.</div>
             <div style='font-size:12px;color:#8a817c'>${msg.replace(/</g, "&lt;")}</div>
             <div style='margin-top:14px;font-size:12px;color:#8a817c'>Your data is safe — keep editing and it&#39;ll retry on the next change.</div>
           </div>`,
        );
      } finally {
        // Only clear loading if this is still the latest request
        if (inFlightRef.current === controller) {
          setLoading(false);
          inFlightRef.current = null;
        }
      }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [biodata]);

  return (
    <div className="sb-preview-wrap" data-testid="biodata-preview">
      <div className="sb-preview-header">
        <span>
          <strong style={{ color: "var(--sb-primary)" }}>{activeTpl}</strong> · Live preview
        </span>
        <span data-testid="preview-loading-flag">
          {loading ? "Updating…" : error ? "Preview error" : "Up to date"}
        </span>
      </div>
      <iframe
        title="Biodata preview"
        srcDoc={html}
        className="sb-preview-iframe"
        sandbox="allow-same-origin"
        data-testid="preview-iframe"
      />
    </div>
  );
}
